import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import LegalModal from "@/components/LegalModal";

// ─── Types ────────────────────────────────────────────────────────────────────

type Step = 1 | 2 | 3 | 4 | 5;

// Canonical step ↔ URL slug map. Output-only: the slug mirrors resolver-derived
// state and is never read back as trusted input (preserves the advance-only
// invariant — see the URL-reflection effect in OnboardingPage).
const STEP_SLUGS: Record<Step, string> = {
  1: "account",
  2: "verify",
  3: "clinic",
  4: "payment",
  5: "welcome",
};

interface AccountForm {
  email: string;
  password: string;
  confirmPassword: string;
  agreedToTerms: boolean;
}

interface ClinicForm {
  fullName: string;
  clinicName: string;
  clinicType: string;
  teamSize: string;
  state: string;
  timezone: string;
  primaryContactEmail: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CLINIC_TYPES = [
  "Primary Care",
  "Dental",
  "Specialty",
  "Mental Health",
  "Substance Use / Behavioral Health",
  "Multi-Specialty",
  "Urgent Care",
  "Pediatrics",
  "OB/GYN",
  "Other",
];

const TEAM_SIZES = ["1–10 staff", "11–25 staff", "26–50 staff", "51–100 staff", "100+ staff"];

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
  "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire",
  "New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio",
  "Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota",
  "Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia",
  "Wisconsin","Wyoming",
];

const TIMEZONES: { value: string; label: string }[] = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
];

const TIMEZONE_LABELS = TIMEZONES.map((t) => t.label);
const tzLabelToValue = (label: string) => TIMEZONES.find((t) => t.label === label)?.value ?? "America/New_York";
const tzValueToLabel = (value: string) => TIMEZONES.find((t) => t.value === value)?.label ?? "Eastern Time (ET)";

// ─── Onboarding resume marker (UX continuity only — no secrets) ─────────────────
// Disambiguates "no session → Step 1" from "no session, awaiting verification →
// Step 2" across refresh / cross-tab. Carries only the email — never a password
// or token (OWASP A04: don't stash sensitive material client-side). Loss is
// benign: lands on Step 1, re-enter email, server sends a fresh link.
const ONBOARDING_MARKER_KEY = "veracord:onboarding:awaitingVerification";

const readAwaitingVerification = (): string | null => {
  try {
    const raw = sessionStorage.getItem(ONBOARDING_MARKER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.email === "string" ? parsed.email : null;
  } catch {
    return null;
  }
};

const clearAwaitingVerification = (): void => {
  try {
    sessionStorage.removeItem(ONBOARDING_MARKER_KEY);
  } catch {
    // sessionStorage unavailable — benign
  }
};

// ─── Step Indicator ───────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: Step }) {
  const steps = [
    { n: 1, label: "Create Account" },
    { n: 2, label: "Verify Email" },
    { n: 3, label: "Clinic Profile" },
    { n: 4, label: "Payment" },
    { n: 5, label: "Welcome" },
  ];

  const springTransition = { type: "spring" as const, stiffness: 350, damping: 25 };

  return (
    <div className="flex items-start justify-center mb-10 w-full">
      {steps.map((s, i) => {
        const isActive = s.n === current;
        const isCompleted = s.n < current;

        return (
          <div key={s.n} className="flex items-start">
            {/* Each step gets a fixed-width column so the group stays centered */}
            <div className="flex flex-col items-center" style={{ width: 'clamp(44px, 12vw, 56px)' }}>
              <AnimatePresence mode="wait">
                {isActive ? (
                  <motion.div
                    key={`active-${s.n}`}
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.5, opacity: 0 }}
                    transition={springTransition}
                    className="flex flex-col items-center"
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 border-[#339999] text-[#339999] bg-transparent">
                      {s.n}
                    </div>
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="mt-1.5 text-xs text-[#339999] text-center leading-tight"
                      style={{ maxWidth: 'clamp(40px, 11vw, 48px)' }}
                    >
                      {s.label}
                    </motion.span>
                  </motion.div>
                ) : (
                  <motion.div
                    key={`dot-${s.n}`}
                    initial={{ scale: 1.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 1.5, opacity: 0 }}
                    transition={springTransition}
                    className="flex flex-col items-center"
                  >
                    <div className="flex items-center justify-center h-8">
                      <div
                        className="rounded-full"
                        style={{
                          width: isCompleted ? 6 : 8,
                          height: isCompleted ? 6 : 8,
                          backgroundColor: isCompleted ? "#339999" : "rgba(255,255,255,0.2)",
                        }}
                      />
                    </div>
                    <span className="mt-1.5 text-[10px] text-white/40 text-center leading-tight" style={{ maxWidth: 'clamp(40px, 11vw, 48px)' }}>
                      {s.label}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Connector line */}
            {i < steps.length - 1 && (
              <div
                className={`h-px ${isCompleted ? "bg-[#339999]" : "bg-white/15"}`}
                style={{ width: 'clamp(8px, 3vw, 16px)', marginTop: 16 }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Input Components ─────────────────────────────────────────────────────────

function Label({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-sm font-medium text-white/70 mb-1.5">{children}</label>
  );
}

function Input({
  type = "text",
  value,
  onChange,
  placeholder,
  disabled,
  autoComplete,
}: {
  type?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  autoComplete?: string;
}) {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";

  return (
    <div className="relative">
      <input
        type={isPassword && showPassword ? "text" : type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        className={`w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/15 text-white placeholder-white/25 focus:outline-none focus:border-[#339999] focus:ring-1 focus:ring-[#339999]/40 transition-all text-sm disabled:opacity-50 ${isPassword ? "pr-10" : ""}`}
      />
      {isPassword && (
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
          tabIndex={-1}
        >
          {showPassword ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
        </button>
      )}
    </div>
  );
}

function Select({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/15 text-white focus:outline-none focus:border-[#339999] focus:ring-1 focus:ring-[#339999]/40 transition-all text-sm appearance-none cursor-pointer"
      style={{ colorScheme: "dark" }}
    >
      <option value="" disabled className="bg-[#00022e]">
        {placeholder}
      </option>
      {options.map((o) => (
        <option key={o} value={o} className="bg-[#00022e]">
          {o}
        </option>
      ))}
    </select>
  );
}

function ErrorMsg({ msg }: { msg: string }) {
  if (!msg) return null;
  return (
    <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      {msg}
    </div>
  );
}

function PrimaryButton({
  children,
  onClick,
  loading,
  disabled,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  loading?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className="w-full py-3 rounded-lg bg-[#339999] hover:bg-[#2a8080] text-white font-semibold text-sm transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
    >
      {loading && (
        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {children}
    </button>
  );
}

// ─── Step 1: Create Account ───────────────────────────────────────────────────

function Step1({
  prefillEmail,
  clinicName,
  onNext,
}: {
  prefillEmail: string;
  clinicName: string;
  onNext: (email: string, password: string) => void;
}) {
  const [form, setForm] = useState<AccountForm>({
    email: prefillEmail,
    password: "",
    confirmPassword: "",
    agreedToTerms: false,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tosOpen, setTosOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  // Sync prefillEmail when it arrives from query params (after initial render)
  useEffect(() => {
    if (prefillEmail && !form.email) {
      setForm((f) => ({ ...f, email: prefillEmail }));
    }
  }, [prefillEmail]);

  const set = (key: keyof AccountForm) => (value: string | boolean) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    setError("");

    if (!form.email) return setError("Email is required.");
    if (form.password.length < 8) return setError("Password must be at least 8 characters.");
    if (form.password !== form.confirmPassword) return setError("Passwords do not match.");
    if (!form.agreedToTerms) return setError("You must agree to the Terms of Service.");

    setLoading(true);

    // Create user via edge function (bypasses default Supabase email)
    const { data, error: fnError } = await supabase.functions.invoke("send-auth-email", {
        body: {
          type: "signup",
          email: form.email,
          password: form.password,
          redirect_to: `${window.location.origin}/onboarding`,
          ...(clinicName ? { clinic_name: clinicName } : {}),
        },
      });

    if (fnError) {
      console.error("send-auth-email invoke error:", fnError);
      setLoading(false);
      setError(`Edge function error: ${fnError.message || JSON.stringify(fnError)}`);
      return;
    }

    if (data?.error) {
      console.error("send-auth-email returned error:", data.error);
      setLoading(false);
      setError(`${data.error}`);
      return;
    }

    setLoading(false);

    // Write resume marker so a refresh while waiting on Step 2 lands back here,
    // not on Step 1. Carries email only — never a password (OWASP A04).
    try {
      sessionStorage.setItem(
        ONBOARDING_MARKER_KEY,
        JSON.stringify({ email: form.email })
      );
    } catch {
      // sessionStorage unavailable (private browsing edge case) — benign,
      // marker loss just means refresh lands on Step 1 and Phase 1 resends.
    }

    // Immediately transition to Step 2
    onNext(form.email, form.password);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Email Address</Label>
        <Input
          type="email"
          value={form.email}
          onChange={set("email")}
          placeholder="you@yourclinic.com"
          autoComplete="email"
        />
      </div>

      <div>
        <Label>Password</Label>
        <Input
          type="password"
          value={form.password}
          onChange={set("password")}
          placeholder="At least 8 characters"
          autoComplete="new-password"
        />
      </div>

      <div>
        <Label>Confirm Password</Label>
        <Input
          type="password"
          value={form.confirmPassword}
          onChange={set("confirmPassword")}
          placeholder="Repeat password"
          autoComplete="new-password"
        />
      </div>

      <div className="flex items-start gap-3 pt-1">
        <button
          onClick={() => set("agreedToTerms")(!form.agreedToTerms)}
          className={`mt-0.5 w-5 h-5 rounded flex-shrink-0 border-2 flex items-center justify-center transition-all ${
            form.agreedToTerms
              ? "bg-[#339999] border-[#339999]"
              : "bg-transparent border-white/30"
          }`}
        >
          {form.agreedToTerms && (
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>
        <span className="text-sm text-white/55 leading-snug">
          I agree to Veracord's{" "}
          <button type="button" onClick={() => setTosOpen(true)} className="text-[#339999] underline hover:text-[#339999]/80">
            Terms of Service
          </button>{" "}
          and{" "}
          <button type="button" onClick={() => setPrivacyOpen(true)} className="text-[#339999] underline hover:text-[#339999]/80">
            Privacy Policy
          </button>
        </span>
      </div>

      {error && <ErrorMsg msg={error} />}

      <div className="pt-2">
        <PrimaryButton onClick={handleSubmit} loading={loading}>
          Create Account
        </PrimaryButton>
      </div>

      <LegalModal open={tosOpen} onClose={() => setTosOpen(false)} title="Terms of Service">
        <TermsOfServiceContent />
      </LegalModal>
      <LegalModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Privacy Policy">
        <PrivacyPolicyContent />
      </LegalModal>
    </div>
  );
}

function TermsOfServiceContent() {
  return (
    <div className="space-y-4">
      <p className="font-semibold text-gray-900">Veracord™ Terms of Service</p>
      <p className="text-xs text-gray-500">Effective Date: March 2, 2026</p>
      <p>Welcome to Veracord™ — The System of Record. These Terms of Service govern access to and use of the Veracord platform operated by LD7 Media LLC ("Veracord," "we," "our," or "us"). By creating an account or using the Service, you agree to these Terms. If you do not agree, do not use the Service.</p>
      <h3 className="font-semibold text-gray-900">1. The Service</h3>
      <p>Veracord is a policy compliance and audit-readiness system built for healthcare clinics. The Service provides: policy storage and version control, immutable locking of approved policies, staff acknowledgment tracking, review scheduling, audit-ready export generation, and optional AI-assisted policy drafting referencing uploaded documents. Veracord is a System of Record. It is not a legal advisor, compliance certification authority, regulatory body, EMR, training platform, or risk scoring system. Final responsibility for regulatory compliance remains with the clinic.</p>
      <h3 className="font-semibold text-gray-900">2. Account Responsibility</h3>
      <p>You are responsible for maintaining accurate account information, protecting login credentials, assigning appropriate internal user permissions, and ensuring authorized use within your organization. You are responsible for all activity under your account.</p>
      <h3 className="font-semibold text-gray-900">3. Data Ownership</h3>
      <p>You retain ownership of all policies, documents, and materials uploaded to Veracord. Veracord does not claim ownership of your content. You grant Veracord a limited license to store, process, render, secure, and back up your data solely for the purpose of operating the Service.</p>
      <h3 className="font-semibold text-gray-900">4. Data Integrity & Immutability</h3>
      <p>Once a policy is approved and locked within Veracord, it becomes immutable, cannot be retroactively edited, and version history is preserved permanently. You acknowledge that locked records cannot be altered or deleted except in accordance with system design and applicable law.</p>
      <h3 className="font-semibold text-gray-900">5. Policy Builder</h3>
      <p>Policy Builder is an optional drafting assistant, powered by Vera. Its My Sources mode references only documents uploaded by the clinic and does not access public web data. Its Research mode may search the web for regulatory guidance, clearly labeled as such. In both modes, Vera does not make compliance decisions and does not replace human review. You remain fully responsible for reviewing and approving all drafted content.</p>
      <h3 className="font-semibold text-gray-900">6. Prohibited Use</h3>
      <p>You may not use the Service to store unlawful content, store patient medical records (unless explicitly supported in writing), violate healthcare privacy regulations, reverse engineer the platform, or circumvent system safeguards.</p>
      <h3 className="font-semibold text-gray-900">7. Subscription & Payment</h3>
      <p>Veracord operates on a per-clinic monthly subscription. Fees are billed in advance. Failure to pay may result in account suspension. Veracord reserves the right to update pricing with advance notice.</p>
      <h3 className="font-semibold text-gray-900">8. Security</h3>
      <p>Veracord implements commercially reasonable security safeguards including encrypted data storage, role-based access controls, and secure hosting infrastructure. No system is guaranteed to be completely secure. You are responsible for maintaining appropriate internal security practices.</p>
      <h3 className="font-semibold text-gray-900">9. Availability</h3>
      <p>We strive for high availability but do not guarantee uninterrupted access. Scheduled maintenance or unforeseen outages may occur.</p>
      <h3 className="font-semibold text-gray-900">10. Termination</h3>
      <p>You may terminate your subscription at any time. Upon termination, access to the Service will end and data retention policies will apply as stated in our Privacy Policy.</p>
      <h3 className="font-semibold text-gray-900">11. Limitation of Liability</h3>
      <p>To the maximum extent permitted by law, Veracord shall not be liable for regulatory penalties, audit failures, business losses, or indirect or consequential damages. Veracord provides documentation tools. Compliance outcomes depend on proper use by the clinic.</p>
      <h3 className="font-semibold text-gray-900">12. Governing Law</h3>
      <p>These Terms are governed by the laws of the State of Florida, without regard to conflict of law principles.</p>
      <h3 className="font-semibold text-gray-900">13. Updates</h3>
      <p>We may update these Terms from time to time. Continued use of the Service constitutes acceptance of the revised Terms.</p>
    </div>
  );
}

function PrivacyPolicyContent() {
  return (
    <div className="space-y-4">
      <p className="font-semibold text-gray-900">Veracord™ Privacy Policy</p>
      <p className="text-xs text-gray-500">Effective Date: March 2, 2026</p>
      <p>Veracord respects your privacy. This Privacy Policy explains how we collect, use, and protect information when you use the Veracord platform.</p>
      <h3 className="font-semibold text-gray-900">1. Information We Collect</h3>
      <p>Account information (name, email, clinic name, role). Usage information (login activity, acknowledgment timestamps, system interaction logs). Uploaded content (policies, regulatory documents, internal compliance documentation). Veracord does not collect patient medical records unless explicitly supported and contracted.</p>
      <h3 className="font-semibold text-gray-900">2. How We Use Information</h3>
      <p>To provide and operate the Service, generate audit reports, maintain immutable records, secure the platform, and improve product performance. We do not sell your data. We do not use your data for advertising. We do not use clinic policy content to train public AI models.</p>
      <h3 className="font-semibold text-gray-900">3. Data Security</h3>
      <p>We implement encryption at rest and in transit, role-based access controls, secure cloud hosting, and audit logging. Access is restricted to authorized personnel.</p>
      <h3 className="font-semibold text-gray-900">4. Data Retention</h3>
      <p>We retain data while your account is active, as required for compliance record integrity, and as required by law. You may request export of your data prior to termination.</p>
      <h3 className="font-semibold text-gray-900">5. Data Sharing</h3>
      <p>We do not share clinic data with third parties except payment processors, secure infrastructure providers, and legal requirements (court order, subpoena). We do not sell or monetize clinic data.</p>
      <h3 className="font-semibold text-gray-900">6. AI Data Handling</h3>
      <p>Documents uploaded for AI drafting are processed within Veracord's controlled environment, are not used to train external AI systems, and remain confidential.</p>
      <h3 className="font-semibold text-gray-900">7. Your Rights</h3>
      <p>You may access your account data, update account information, request data export, and request account deletion (subject to legal record requirements).</p>
      <h3 className="font-semibold text-gray-900">8. Children</h3>
      <p>Veracord is not intended for individuals under 18.</p>
      <h3 className="font-semibold text-gray-900">9. Updates</h3>
      <p>We may update this Privacy Policy periodically. Continued use of the Service constitutes acceptance of changes.</p>
    </div>
  );
}


function Step2({
  email,
  onNext,
}: {
  email: string;
  onNext: () => void;
}) {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  // Advance to Step 3 when a session appears — AuthContext fires onAuthStateChange
  // cross-tab (persisted session) so verifying in the email tab advances this tab
  // automatically. The parent Step 2->3 watcher does the same; this is the
  // within-Step2 signal that calls onNext() to keep the prop contract intact.
  const { user } = useAuth();
  useEffect(() => {
    if (user) {
      onNext();
    }
  }, [user, onNext]);

  const handleResend = async () => {
    setResending(true);
    try {
      await supabase.functions.invoke("send-auth-email", {
        body: {
          type: "resend_verification",
          email,
          redirect_to: `${window.location.origin}/onboarding`,
        },
      });
    } catch {
      // Silently fail
    }
    setResending(false);
    setResent(true);
    setTimeout(() => setResent(false), 5000);
  };

  return (
    <div className="text-center space-y-6">
      {/* Animated envelope icon with pulse */}
      <div className="flex justify-center">
        <div className="relative">
          <div className="w-20 h-20 rounded-2xl bg-[#339999]/10 border border-[#339999]/20 flex items-center justify-center">
            <svg className="w-10 h-10 text-[#339999]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          {/* Pulse ring */}
          <div className="absolute inset-0 rounded-2xl border-2 border-[#339999]/40 animate-ping" style={{ animationDuration: '2s' }} />
        </div>
      </div>

      <div>
        <p className="text-white/70 text-sm leading-relaxed">
          We sent a verification link to
        </p>
        <p className="text-white font-medium mt-1">{email}</p>
        <p className="text-white/50 text-sm mt-3 leading-relaxed">
          Click the link in that email to verify your account.<br />
          This page will advance automatically once confirmed.
        </p>
      </div>

      {/* Waiting indicator */}
      <div className="flex items-center justify-center gap-2 text-[#339999]/70 text-xs">
        <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        Waiting for confirmation…
      </div>

      <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-left">
        <p className="text-sm text-white/60 leading-relaxed">
          Clicking the link opens a <span className="text-white/80 font-medium">new tab</span> to continue setup.
          If that happened, finish there — this tab is safe to close.
        </p>
      </div>

      <div className="space-y-3">
        <button
          onClick={handleResend}
          disabled={resending || resent}
          className="w-full py-2.5 text-sm text-white/50 hover:text-white/80 transition-colors disabled:opacity-40"
        >
          {resent ? "✓ Email sent!" : resending ? "Sending..." : "Resend verification email"}
        </button>
      </div>

      <p className="text-white/30 text-xs">
        Check your spam folder if you don't see it within a minute.
      </p>
    </div>
  );
}

// ─── Step 3: Clinic Profile ───────────────────────────────────────────────────

function Step3({
  prefillClinicName,
  prefillEmail,
  sessionId,
  onNext,
}: {
  prefillClinicName: string;
  prefillEmail: string;
  sessionId: string | null;
  onNext: () => void;
}) {
  const [form, setForm] = useState<ClinicForm>({
    fullName: "",
    clinicName: prefillClinicName,
    clinicType: "",
    teamSize: "",
    state: "",
    timezone: "America/New_York",
    primaryContactEmail: prefillEmail,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (key: keyof ClinicForm) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    setError("");

    if (!form.fullName.trim()) return setError("Your full name is required.");
    if (!form.clinicName.trim()) return setError("Clinic name is required.");
    if (!form.clinicType) return setError("Please select a clinic type.");
    if (!form.teamSize) return setError("Please select your team size.");
    if (!form.state) return setError("Please select your state.");
    if (!form.primaryContactEmail.trim()) return setError("Contact email is required.");

    setLoading(true);

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      setLoading(false);
      setError("Your session expired. Please sign in again.");
      return;
    }

    const clinicInsert: Record<string, any> = {
      user_id: user.id,
      name: form.clinicName,
      clinic_type: form.clinicType,
      team_size: form.teamSize,
      state: form.state,
      timezone: form.timezone,
      contact_email: form.primaryContactEmail,
    };

    const { error: dbError } = await supabase
      .from("clinics")
      .insert(clinicInsert as any);

    if (dbError) {
      // 23505 = unique constraint violation: clinic already exists for this user
      // (two-tab submit race). Treat as success — fall through to fetch the
      // existing clinic row. (DDIA: idempotent writes make concurrent retries safe.)
      if (dbError.code !== "23505") {
        setLoading(false);
        setError("Failed to save clinic. Please try again.");
        console.error(dbError);
        return;
      }
      console.warn("Clinic already exists for user (23505) — continuing with existing row.");
    }

    // Fetch the newly created clinic to get its id
    const { data: clinicData, error: clinicFetchError } = await supabase
      .from("clinics")
      .select("id")
      .eq("user_id", user?.id)
      .single();

    if (clinicFetchError || !clinicData) {
      setLoading(false);
      setError("Failed to retrieve clinic. Please try again.");
      console.error(clinicFetchError);
      return;
    }

    // Create the profile row so the auth guard lets the user through
    const { error: profileError } = await supabase
      .from("profiles")
      .insert({
        user_id: user?.id,
        clinic_id: clinicData.id,
        full_name: form.fullName.trim(),
        email: form.primaryContactEmail,
      });

    if (profileError) {
      // 23505: profile already exists (two-tab race). Treat as success — continue.
      if (profileError.code !== "23505") {
        setLoading(false);
        setError("Failed to create profile. Please try again.");
        console.error(profileError);
        return;
      }
      console.warn("Profile already exists for user (23505) — continuing.");
    }

    // Assign admin role via SECURITY DEFINER RPC — direct table
    // insert is no longer permitted by RLS (privilege escalation fix)
    const { error: roleError } = await supabase
      .rpc('assign_initial_admin_role');

    if (roleError) {
      setLoading(false);
      setError("Failed to assign role. Please try again.");
      console.error(roleError);
      return;
    }

    // Insert owner into clinic_members so they appear in Team Management
    const { error: memberError } = await supabase
      .from("clinic_members")
      .insert({
        clinic_id: clinicData.id,
        name: form.fullName.trim() || form.primaryContactEmail.split("@")[0],
        email: form.primaryContactEmail,
        role: "owner",
      });

    if (memberError) {
      console.error("Failed to insert owner into clinic_members:", memberError);
      // Non-blocking — continue onboarding even if this fails
    }

    // Seed default policy categories for the new clinic
    const defaultCategories = ['Clinical', 'Compliance', 'HR', 'Privacy', 'Safety'];
    const categoryRows = defaultCategories.map(name => ({
      clinic_id: clinicData.id,
      name,
    }));
    const { error: catError } = await supabase
      .from('clinic_categories')
      .insert(categoryRows);
    if (catError) {
      console.error('Failed to seed default categories:', catError);
      // Non-blocking
    }

    setLoading(false);
    onNext();
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Your Full Name</Label>
        <Input
          value={form.fullName}
          onChange={set("fullName")}
          placeholder="Dr. Jane Smith"
          autoComplete="name"
        />
      </div>

      <div>
        <Label>Clinic Name</Label>
        <Input
          value={form.clinicName}
          onChange={set("clinicName")}
          placeholder="Sunrise Family Medicine"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Clinic Type</Label>
          <Select
            value={form.clinicType}
            onChange={set("clinicType")}
            options={CLINIC_TYPES}
            placeholder="Select type"
          />
        </div>
        <div>
          <Label>Team Size</Label>
          <Select
            value={form.teamSize}
            onChange={set("teamSize")}
            options={TEAM_SIZES}
            placeholder="Select size"
          />
        </div>
      </div>

      <div>
        <Label>State</Label>
        <Select
          value={form.state}
          onChange={set("state")}
          options={US_STATES}
          placeholder="Select state"
        />
      </div>

      <div>
        <Label>Timezone</Label>
        <Select
          value={tzValueToLabel(form.timezone)}
          onChange={(label: string) => setForm((f) => ({ ...f, timezone: tzLabelToValue(label) }))}
          options={TIMEZONE_LABELS}
          placeholder="Select timezone"
        />
      </div>

      <div>
        <Label>Primary Compliance Contact Email</Label>
        <Input
          type="email"
          value={form.primaryContactEmail}
          onChange={set("primaryContactEmail")}
          placeholder="compliance@yourclinic.com"
          autoComplete="email"
        />
      </div>

      {error && <ErrorMsg msg={error} />}

      <div className="pt-2">
        <PrimaryButton onClick={handleSubmit} loading={loading}>
          Save & Continue
        </PrimaryButton>
      </div>
    </div>
  );
}

// ─── Step 4: Payment ──────────────────────────────────────────────────────────

function Step4Payment({
  email,
  clinicName,
  onNext,
}: {
  email: string;
  clinicName: string;
  onNext: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [billingPeriod, setBillingPeriod] = useState<'annual' | 'monthly'>('annual');
  const [showPromo, setShowPromo] = useState(false);
  const [promoCode, setPromoCode] = useState("");

  const handleCheckout = async () => {
    setError("");
    setLoading(true);

    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const url = `https://${projectId}.supabase.co/functions/v1/create-checkout-session`;

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          clinic_name: clinicName,
          billing_period: billingPeriod,
          ...(promoCode.trim() && { promo_code: promoCode.trim() }),
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.url) {
        throw new Error(data.error || "Failed to create checkout session");
      }

      window.location.href = data.url;
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#339999]/10 border border-[#339999]/20 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-[#339999]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        </div>
        <p className="text-white/70 text-sm leading-relaxed">
          You're almost there! Complete your subscription to activate your account.
        </p>
        <p className="text-white font-semibold text-lg mt-2">
          {billingPeriod === 'annual' ? '$2,490 / year — 2 months free' : '$249 / month'}
        </p>
        {billingPeriod === 'annual' && (
          <p className="text-[#339999]/80 text-xs mt-1 font-medium">2 months free · Founding rate — first 100 clinics</p>
        )}
        <p className="text-white/40 text-xs mt-1">Unlimited policies · Unlimited staff · Cancel anytime</p>
      </div>

      {/* Billing toggle */}
      <div className="flex rounded-lg border border-white/10 overflow-hidden">
        <button
          type="button"
          onClick={() => setBillingPeriod('annual')}
          className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
            billingPeriod === 'annual'
              ? 'bg-[#339999]/20 border-r border-[#339999]/40 text-white'
              : 'bg-white/5 border-r border-white/10 text-white/50 hover:text-white/70'
          }`}
        >
          Annual
        </button>
        <button
          type="button"
          onClick={() => setBillingPeriod('monthly')}
          className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
            billingPeriod === 'monthly'
              ? 'bg-[#339999]/20 text-white'
              : 'bg-white/5 text-white/50 hover:text-white/70'
          }`}
        >
          Monthly
        </button>
      </div>

      {/* Promo code */}
      <div className="text-center">
        {!showPromo ? (
          <button
            type="button"
            onClick={() => setShowPromo(true)}
            className="text-white/50 hover:text-white/70 text-xs underline cursor-pointer transition-colors"
          >
            Have a promo code?
          </button>
        ) : (
          <Input
            value={promoCode}
            onChange={(v) => setPromoCode(v)}
            placeholder="Enter promo code"
          />
        )}
      </div>

      {error && <ErrorMsg msg={error} />}

      <div className="pt-2">
        <PrimaryButton onClick={handleCheckout} loading={loading}>
          {loading ? "Redirecting to Stripe…" : "Proceed to Payment"}
        </PrimaryButton>
      </div>

      <p className="text-white/30 text-xs text-center">
        You'll be redirected to Stripe to complete payment securely.
      </p>
    </div>
  );
}

// ─── Step 5: Welcome ──────────────────────────────────────────────────────────

function Step5({ clinicName }: { clinicName: string }) {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();
  const queryClient = useQueryClient();

  const nextSteps = [
    {
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      ),
      title: "Add Team Members",
      desc: "Invite staff so they can acknowledge policies from their inbox.",
    },
    {
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      ),
      title: "Create Your First Policy",
      desc: "Upload an existing policy or draft one with Policy Builder.",
    },
    {
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      ),
      title: "Set Review Schedules",
      desc: "Configure automatic reminders so policies never lapse unnoticed.",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Success icon */}
      <div className="flex flex-col items-center text-center pt-2">
        <div className="w-16 h-16 rounded-full bg-[#339999]/15 border border-[#339999]/30 flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-[#339999]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-white">
          {clinicName || "Your clinic"} is ready.
        </h3>
        <p className="text-white/50 text-sm mt-1.5 leading-relaxed max-w-xs">
          Your Veracord account is active. Here's what to do first to be audit-ready.
        </p>
      </div>

      {/* Next steps */}
      <div className="space-y-2.5">
        {nextSteps.map((step, i) => (
          <div
            key={i}
            className="flex gap-3.5 p-3.5 rounded-xl bg-white/4 border border-white/8 hover:border-white/15 transition-colors"
          >
            <div className="w-9 h-9 rounded-lg bg-[#339999]/10 flex-shrink-0 flex items-center justify-center">
              <svg className="w-5 h-5 text-[#339999]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {step.icon}
              </svg>
            </div>
            <div>
              <p className="text-white text-sm font-medium">{step.title}</p>
              <p className="text-white/45 text-xs mt-0.5 leading-relaxed">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="pt-2">
        <PrimaryButton onClick={async () => {
          await refreshProfile();
          await queryClient.invalidateQueries({ queryKey: ['subscription'] });
          // ProtectedRoute's needsOnboarding gate reads ['subscription-status'],
          // not ['subscription']. SoftCloseBanner (always mounted) may have cached
          // a pre-webhook 'incomplete' within its 60s staleTime on the Stripe
          // return load; awaiting this refetch prevents the /dashboard ↔
          // /onboarding redirect loop (F-S68-3 follow-up).
          await queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
          navigate("/dashboard");
        }}>
          Go to Dashboard
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </PrimaryButton>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function OnboardingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, loading, refreshProfile } = useAuth();
  const [step, setStep] = useState<Step>(1);
  const [returningFromPayment, setReturningFromPayment] = useState(false);
  const [email, setEmail] = useState(() => searchParams.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [clinicName, setClinicName] = useState(() => searchParams.get("clinic_name") ?? "");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [stripeReturnPending, setStripeReturnPending] = useState<boolean>(
    () => !!searchParams.get("session_id")
  );
  const resolvingRef = useRef(false);

  // Resolver may only ever ADVANCE the wizard — never pull the user backward or
  // fight an explicit, user-driven forward navigation. (DDIA: idempotent,
  // convergent state derivation; advance-only makes re-runs safe.)
  const applyStep = useCallback((target: Step) => {
    setStep((prev) => (target > prev ? target : prev));
  }, []);

  // Derive the wizard step from authoritative auth + subscription state.
  // Safe to re-run on focus/visibility — advance-only makes concurrent tabs converge.
  const resolveOnboardingStep = useCallback(
    async (opts?: {
      activeUser?: typeof user;
      activeProfile?: typeof profile;
    }) => {
      if (searchParams.get("session_id")) return;
      if (resolvingRef.current) return;

      const activeUser = opts?.activeUser ?? user;
      if (!activeUser) {
        const awaiting = readAwaitingVerification();
        if (awaiting) {
          setEmail((e) => e || awaiting);
          applyStep(2);
        }
        return;
      }

      clearAwaitingVerification();
      resolvingRef.current = true;
      try {

      const activeProfile =
        opts?.activeProfile !== undefined ? opts.activeProfile : profile;

      if (!activeProfile) {
        setEmail((e) => e || activeUser.email || "");
        // Backfill clinic name from auth metadata if stashed at signup
        // (covers the Gmail-tab scenario where clinic_name is unknown in memory).
        // Best-effort hint only — Step 3 form remains editable; clinics table is authoritative.
        const metaClinicName = activeUser.user_metadata?.clinic_name;
        if (metaClinicName && typeof metaClinicName === "string") {
          setClinicName((c) => c || metaClinicName);
        }
        applyStep(3);
        return;
      }

      setEmail((e) => e || activeProfile.email || activeUser.email || "");

      // F-S68-4: read the same subscription_status column ProtectedRoute reads
      // (Soft-Close spec §4.1 single source of truth) instead of a live Stripe
      // round-trip. past_due is included: writes stay open during the grace
      // window (stripe-webhook contract), so the clinic isn't onboarding-active.
      const { data: clinicRow, error: subError } = await supabase
        .from("clinics")
        .select("subscription_status")
        .eq("id", activeProfile.clinic_id)
        .maybeSingle();
      if (subError) {
        console.error("Onboarding resume: subscription check failed:", subError);
      }
      const isActive =
        clinicRow?.subscription_status === "active" ||
        clinicRow?.subscription_status === "past_due";
      if (isActive) {
        navigate("/dashboard", { replace: true });
        return;
      }

      const { data: clinic } = await supabase
        .from("clinics")
        .select("name")
        .eq("id", activeProfile.clinic_id)
        .maybeSingle();
      if (clinic?.name) {
        const name = clinic.name;
        setClinicName((c) => c || name);
      }
      applyStep(4);
      } finally {
        resolvingRef.current = false;
      }
    },
    [user?.id, profile?.clinic_id, searchParams, navigate, applyStep]
  );

  // Re-fetch profile/subscription from server and converge step (multi-tab safe).
  const syncOnboardingFromServer = useCallback(async () => {
    if (searchParams.get("session_id")) return;

    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (!currentUser) {
      await resolveOnboardingStep({ activeUser: null, activeProfile: null });
      return;
    }

    clearAwaitingVerification();
    const freshProfile = await refreshProfile();
    await resolveOnboardingStep({
      activeUser: currentUser,
      activeProfile: freshProfile,
    });
  }, [searchParams, refreshProfile, resolveOnboardingStep]);

  // ── Resume resolver (after AuthContext settles) ────────────────────────────
  useEffect(() => {
    if (searchParams.get("session_id")) return;
    if (loading) return;
    void resolveOnboardingStep();
  }, [loading, user?.id, profile?.clinic_id, searchParams, resolveOnboardingStep]);

  // ── Cross-tab / post-verification sync on focus or tab visibility ──────────
  useEffect(() => {
    if (searchParams.get("session_id")) return;

    const handleSync = () => {
      void syncOnboardingFromServer();
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        handleSync();
      }
    };

    window.addEventListener("focus", handleSync);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("focus", handleSync);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [searchParams, syncOnboardingFromServer]);

  // Pre-fill from query params (from /get-started) or Stripe session
  useEffect(() => {
    const sessionId = searchParams.get("session_id");

    if (sessionId) setSessionId(sessionId);

    if (!sessionId) return;

    // Retrieve pre-fill data via edge function if available
    supabase.functions
      .invoke("get-checkout-session", { body: { session_id: sessionId } })
      .then(({ data }) => {
        if (!data || data.payment_status !== "paid") {
          console.warn("Stripe session returned but payment_status is not 'paid':", data?.payment_status);
          setStep(4);
          setStripeReturnPending(false);
          return;
        }
        if (data?.email) setEmail(data.email);
        if (data?.clinic_name) setClinicName(data.clinic_name);
        setStep(5);
        setReturningFromPayment(true);
        setStripeReturnPending(false);
      })
      .catch(() => {
        // Silently fail — form still works without prefill
        setStripeReturnPending(false);
      });

    supabase.functions
      .invoke("link-stripe-customer", { body: { session_id: sessionId } })
      .catch((err) => {
        console.error("Failed to link Stripe customer:", err);
        // Non-blocking — stripe_customer_id can be reconciled manually if needed
      });
  }, []);

  // ── Reflect the resolved step in the URL (output-only) ─────────────────────
  // The slug mirrors resolver-derived state; it is never read back as input.
  // Seeding step from an untrusted slug would defeat the advance-only invariant
  // (an unauthenticated user typing /onboarding/payment could strand on Step 4,
  // since applyStep can't pull backward). Replace-only: no regressive history, so
  // Back exits the flow cleanly and re-entry resumes via the resolver. Stands
  // down on the Stripe return path so ?session_id= is preserved.
  useEffect(() => {
    if (searchParams.get("session_id")) return;
    // Wait for the resolver to settle before writing the URL — reflecting the
    // provisional default step during load would flicker the address bar
    // (e.g. /onboarding/clinic → /account → /clinic on a deep refresh).
    if (loading) return;
    const target = `/onboarding/${STEP_SLUGS[step]}`;
    if (location.pathname !== target) {
      navigate(target, { replace: true });
    }
  }, [step, location.pathname, searchParams, navigate, loading]);

  // Step titles & subtitles
  const stepMeta: Record<Step, { title: string; subtitle: string }> = {
    1: {
      title: "Create your account",
      subtitle: "Set up secure access to your Veracord dashboard.",
    },
    2: {
      title: "Verify your email",
      subtitle: "One quick step to confirm your identity.",
    },
    3: {
      title: "Tell us about your clinic",
      subtitle: "This helps us configure your compliance environment.",
    },
    4: {
      title: "Activate your subscription",
      subtitle: "One flat rate. Unlimited everything.",
    },
    5: {
      title: "You're all set",
      subtitle: "Veracord is ready. Your audit panic ends here.",
    },
  };

  // While AuthContext is settling (and we're not on the Stripe return path), the
  // resolver hasn't classified the entry step yet — show a neutral spinner rather
  // than flashing the default Step 1 content on a deep refresh.
  const resolving = (loading && !searchParams.get("session_id")) || stripeReturnPending;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12"
      style={{
        background: "linear-gradient(135deg, #00022e 0%, #02075d 50%, #00022e 100%)",
      }}
    >
      {/* Subtle mesh gradient overlay */}
      <div
        className="fixed inset-0 pointer-events-none opacity-30"
        style={{
          background: `
            radial-gradient(ellipse 60% 50% at 20% 30%, rgba(51,153,153,0.15) 0%, transparent 70%),
            radial-gradient(ellipse 50% 40% at 80% 70%, rgba(15,54,133,0.2) 0%, transparent 70%)
          `,
        }}
      />

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <span className="text-xl font-semibold tracking-tight" style={{ fontFamily: "'Source Serif Pro', serif" }}>
              Veracord
            </span>
            <span className="text-[10px] font-medium text-white/30 tracking-widest uppercase mt-0.5">
              The System of Record
            </span>
          </a>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl border border-white/10 p-8 overflow-visible"
          style={{
            background: "rgba(255,255,255,0.04)",
            backdropFilter: "blur(20px)",
          }}
        >
          {resolving ? (
            <div className="flex items-center justify-center py-16">
              <svg className="animate-spin w-6 h-6 text-[#339999]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : (
          <>
          {/* Step indicator */}
          <StepIndicator current={step} />

          {/* Step heading */}
          <div className="mb-6 text-center">
            <h2
              className="text-xl font-semibold text-white"
              style={{ fontFamily: "'Source Serif Pro', serif" }}
            >
              {stepMeta[step].title}
            </h2>
            <p className="text-sm text-white/45 mt-1">{stepMeta[step].subtitle}</p>
          </div>

          {/* Step content */}
          {step === 1 && (
            <Step1
              prefillEmail={email}
              clinicName={clinicName}
              onNext={(confirmedEmail, confirmedPassword) => {
                setEmail(confirmedEmail);
                setPassword(confirmedPassword);
                setStep(2);
              }}
            />
          )}

          {step === 2 && (
            <Step2
              email={email}
              onNext={() => setStep(3)}
            />
          )}

          {step === 3 && (
            <Step3
              prefillClinicName={clinicName}
              prefillEmail={email}
              sessionId={sessionId}
              onNext={() => setStep(4)}
            />
          )}

          {step === 4 && (
            <Step4Payment
              email={email}
              clinicName={clinicName}
              onNext={() => setStep(5)}
            />
          )}

          {step === 5 && <Step5 clinicName={clinicName} />}
          </>
          )}
        </div>

        {/* Footer note */}
        {step < 5 && (
          <p className="text-center text-white/25 text-xs mt-6">
            Need help? Email{" "}
            <a href="mailto:support@veracord.co" className="text-white/40 hover:text-white/60 underline">
              support@veracord.co
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
